import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardService, SavedDashboardSummary } from '../services/dashboardService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PermissionGuard from '../components/PermissionGuard';
import AccessDenied from '../components/AccessDenied';
import WorkTypeBadge from '../components/WorkTypeBadge';
import Pagination, { PAGE_SIZE_OPTIONS } from '../components/Pagination';
import opportunityService, { OpportunityScenarioSummary } from '../services/opportunityService';

type WorkKind = 'project' | 'opportunity';

/** A saved project dashboard or opportunity scenario, as one table row. */
interface SavedRow {
  kind: WorkKind;
  id: number;
  label: string;
  workId: number;
  workName: string | null;
  totalCost: number;
  totalEmployeeCosts: number;
  memberCount: number;
  savedAt: string;
}

const fromDashboard = (d: SavedDashboardSummary): SavedRow => ({
  kind: 'project',
  id: d.id,
  label: d.label || '',
  workId: d.projectId,
  workName: d.projectName || null,
  totalCost: d.calculatedCosts.totalCost,
  totalEmployeeCosts: d.calculatedCosts.totalEmployeeCosts,
  memberCount: d.memberCount,
  savedAt: d.savedAt,
});

const fromScenario = (s: OpportunityScenarioSummary): SavedRow => ({
  kind: 'opportunity',
  id: s.id,
  label: s.label,
  workId: s.opportunityId,
  workName: s.opportunityName,
  totalCost: s.calculatedCosts.totalCost,
  totalEmployeeCosts: s.calculatedCosts.totalEmployeeCosts,
  memberCount: s.memberCount,
  savedAt: s.savedAt,
});

type PageFetcher = (limit: number, offset: number) => Promise<{ rows: SavedRow[]; total: number }>;

const fetchDashboardPage: PageFetcher = async (limit, offset) => {
  const data = await DashboardService.getSavedDashboards(limit, offset);
  if (!data) {
    throw new Error('Failed to load saved project dashboards');
  }
  return { rows: data.dashboards.map(fromDashboard), total: data.pagination.total };
};

const fetchScenarioPage: PageFetcher = async (limit, offset) => {
  const data = await opportunityService.listAllScenarios(limit, offset);
  return { rows: data.scenarios.map(fromScenario), total: data.pagination.total };
};

const DEFAULT_PAGE_SIZE = 10;

const readPositiveInt = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * One server-paged list. Page and page size live in the URL (`<prefix>Page`,
 * `<prefix>Size`), so Back from a view or edit returns to the same page and a
 * page can be linked to.
 */
const usePagedList = (prefix: string, fetchPage: PageFetcher) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageKey = `${prefix}Page`;
  const sizeKey = `${prefix}Size`;
  const page = readPositiveInt(searchParams.get(pageKey), 1);
  const requestedSize = readPositiveInt(searchParams.get(sizeKey), DEFAULT_PAGE_SIZE);
  const pageSize = PAGE_SIZE_OPTIONS.includes(requestedSize) ? requestedSize : DEFAULT_PAGE_SIZE;

  const [rows, setRows] = useState<SavedRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const updateParams = useCallback((nextPage: number, nextSize: number) => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      if (nextPage === 1) next.delete(pageKey); else next.set(pageKey, String(nextPage));
      if (nextSize === DEFAULT_PAGE_SIZE) next.delete(sizeKey); else next.set(sizeKey, String(nextSize));
      return next;
    }, { replace: true });
  }, [setSearchParams, pageKey, sizeKey]);
  // setSearchParams changes identity on every URL change, so the fetch effect
  // reads it through a ref - otherwise paging one list would refetch the other.
  const updateParamsRef = useRef(updateParams);
  updateParamsRef.current = updateParams;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPage(pageSize, (page - 1) * pageSize)
      .then((result) => {
        if (cancelled) return;
        const lastPage = Math.max(1, Math.ceil(result.total / pageSize));
        // Past the end (items deleted, or a stale link): jump to the last page,
        // which triggers a fresh fetch.
        if (page > lastPage) {
          updateParamsRef.current(lastPage, pageSize);
          return;
        }
        setRows(result.rows);
        setTotal(result.total);
        setLoaded(true);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(`Error loading ${prefix} page:`, err);
        setError(err?.message || 'Failed to load');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchPage, page, pageSize, reloadToken, prefix]);

  return {
    rows,
    total,
    page,
    pageSize,
    loading,
    loaded,
    error,
    setPage: (next: number) => updateParams(next, pageSize),
    // A new page size starts again from the first page.
    setPageSize: (size: number) => updateParams(1, size),
    reload: () => setReloadToken((token) => token + 1),
  };
};

type PagedList = ReturnType<typeof usePagedList>;

const SavedDashboardsPage: React.FC = () => {
  // Project dashboards and opportunity scenarios come from separate endpoints
  // and are listed and paged separately.
  const projects = usePagedList('project', fetchDashboardPage);
  const opportunities = usePagedList('opportunity', fetchScenarioPage);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleView = (row: SavedRow) => {
    navigate(row.kind === 'project' ? `/saved-dashboard/${row.id}` : `/opportunity-scenario/${row.id}`);
  };

  const handleEdit = (row: SavedRow) => {
    navigate(row.kind === 'project' ? `/whatif?edit=${row.id}` : `/whatif?opportunityScenario=${row.id}`);
  };

  const handleDelete = async (row: SavedRow) => {
    const noun = row.kind === 'project' ? 'dashboard' : 'scenario';
    if (!window.confirm(`Are you sure you want to delete this saved ${noun}?`)) {
      return;
    }

    const key = `${row.kind}:${row.id}`;
    setDeletingKey(key);

    try {
      if (row.kind === 'project') {
        await DashboardService.deleteDashboardData(row.id);
        projects.reload();
      } else {
        await opportunityService.deleteScenario(row.id);
        opportunities.reload();
      }
      alert(`${noun === 'dashboard' ? 'Dashboard' : 'Scenario'} deleted successfully!`);
    } catch (err) {
      console.error(`Error deleting ${noun}:`, err);
      alert(`Failed to delete ${noun}`);
    } finally {
      setDeletingKey(null);
    }
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderTable = (list: PagedList, workLabel: string, ariaLabel: string) => (
    <div className="relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-emerald-100/50">
      <div className={`overflow-x-auto transition-opacity ${list.loading ? 'opacity-50' : ''}`} aria-busy={list.loading}>
        <table className="min-w-full divide-y divide-gray-200" role="table" aria-label={ariaLabel}>
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Label
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {workLabel}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Cost
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Team Members
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Saved At
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {list.rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">
                    {row.label || <span className="text-gray-400 italic">No label</span>}
                  </div>
                  <div className="text-xs text-gray-500">ID: {row.id}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {row.workName || `${workLabel} #${row.workId}`}
                  </div>
                  <div className="text-sm text-gray-500">ID: {row.workId}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-gray-900">
                    {formatCurrency(row.totalCost)}
                  </div>
                  <div className="text-xs text-gray-500">
                    Employee: {formatCurrency(row.totalEmployeeCosts)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row.memberCount} members
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(row.savedAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-3">
                    <button
                      onClick={() => handleView(row)}
                      aria-label={`View ${row.label || row.id}`}
                      className="text-blue-600 hover:text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded px-2 py-1 transition-all"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleEdit(row)}
                      aria-label={`Edit ${row.label || row.id}`}
                      className="text-green-600 hover:text-green-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 rounded px-2 py-1 transition-all"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(row)}
                      disabled={deletingKey === `${row.kind}:${row.id}`}
                      aria-label={`Delete ${row.label || row.id}`}
                      aria-busy={deletingKey === `${row.kind}:${row.id}`}
                      className="text-red-600 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {deletingKey === `${row.kind}:${row.id}` ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={list.page}
        pageSize={list.pageSize}
        totalItems={list.total}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        disabled={list.loading}
        label={`${ariaLabel} pages`}
      />
    </div>
  );

  /** Spinner on first load, error, empty state, or the paged table. */
  const renderSection = (
    list: PagedList,
    workLabel: string,
    ariaLabel: string,
    empty: React.ReactNode
  ) => {
    if (!list.loaded && list.loading) {
      return (
        <div className="flex items-center justify-center h-32 bg-white rounded-lg shadow" role="status" aria-live="polite">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden="true"></div>
          <span className="sr-only">Loading {ariaLabel.toLowerCase()}, please wait</span>
        </div>
      );
    }
    if (list.error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between" role="alert">
          <p className="text-red-800 font-medium">{list.error}</p>
          <button
            type="button"
            onClick={list.reload}
            className="text-sm text-red-700 hover:text-red-900 underline"
          >
            Retry
          </button>
        </div>
      );
    }
    if (list.total === 0) {
      return empty;
    }
    return renderTable(list, workLabel, ariaLabel);
  };

  return (
    <PermissionGuard
      pbsRoles={["admin", "project_owner"]}
      fallback={
        <AccessDenied
          title="Access Required"
          message="Saved project analyses are only available to PBS admins and project owners."
        />
      }
    >
      <div className="space-y-6 bg-gradient-to-br from-white via-emerald-50/40 to-teal-50/30 rounded-xl p-6 shadow-sm border border-emerald-100/50">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Saved Project Analysis</h2>
            <p className="mt-1 text-sm text-gray-600">View, edit, or delete saved cost analyses for projects and opportunities</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            aria-label="Create a new project dashboard"
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
          >
            Create New Dashboard
          </button>
        </div>
      </div>

      {/* Project dashboards */}
      <section className="space-y-3" aria-labelledby="saved-projects-heading">
        <div className="flex items-center gap-2">
          <h3 id="saved-projects-heading" className="text-lg font-semibold text-gray-900">Project dashboards</h3>
          <WorkTypeBadge isOpportunity={false} />
          {projects.loaded && (
            <span className="text-sm text-gray-500">({projects.total})</span>
          )}
        </div>
        {renderSection(
          projects,
          'Project',
          'Saved project dashboards',
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No saved project analyses</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new dashboard.</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/dashboard')}
                aria-label="Create a new project dashboard"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
              >
                Create Dashboard
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Opportunity scenarios */}
      <section className="space-y-3" aria-labelledby="saved-opportunities-heading">
        <div className="flex items-center gap-2">
          <h3 id="saved-opportunities-heading" className="text-lg font-semibold text-gray-900">Opportunity scenarios</h3>
          <WorkTypeBadge isOpportunity />
          {opportunities.loaded && (
            <span className="text-sm text-gray-500">({opportunities.total})</span>
          )}
        </div>
        {renderSection(
          opportunities,
          'Opportunity',
          'Saved opportunity scenarios',
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <h3 className="text-sm font-medium text-gray-900">No saved opportunity scenarios</h3>
            <p className="mt-1 text-sm text-gray-500">Cost prospective work from What-If Analysis → New Opportunities.</p>
          </div>
        )}
      </section>
    </div>
    </PermissionGuard>
  );
};

export default SavedDashboardsPage;
